import { Link } from 'react-router-dom';
import {
  Facebook,
  Instagram,
  Twitter,
  Mail,
  MapPin,
  Eraser,
  Eye,
  ShieldOff,
  Package,
  Building2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { features as featureCatalog } from '@/lib/featuresData';
import { allComparisonLinks } from '@/lib/comparisonsData';

const footerLinks = {
  product: [
    { label: 'All Features', href: '/features' },
    { label: 'Cinematic AI Video', href: '/cinematic-video' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Single Property Kit', href: '/single-property-kit' },
    { label: 'For Real Estate Agents', href: '/real-estate-marketing-software' },
    { label: 'How It Works', href: '/#how-it-works' },
  ],
  resources: [
    { label: 'Blog', href: '/blog' },
    { label: 'Compare', href: '/compare' },
    { label: 'Best Virtual Staging Software', href: '/best-virtual-staging-software' },
    { label: 'Best AI for Real Estate', href: '/best-ai-for-real-estate' },
    { label: 'Help Center', href: '/contact' },
    { label: 'Guides', href: '/blog' },
  ],
  freeTools: [
    { label: 'AI Description Generator', href: '/ai-real-estate-descriptions' },
    { label: 'AI Virtual Staging', href: '/ai-virtual-staging' },
    { label: 'AI Photo Transformation', href: '/ai-photo-transformation' },
    { label: 'Real Estate Marketing Tools', href: '/real-estate-marketing-tools' },
    { label: 'Real Estate Marketing Ideas', href: '/real-estate-marketing-ideas' },
    { label: 'How to Write a Listing Description', href: '/how-to-write-a-real-estate-listing-description' },
  ],
  company: [
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '/contact' },
    { label: 'Careers', href: '/contact' },
    { label: 'Press', href: '/contact' },
  ],
  legal: [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Terms of Service', href: '/terms' },
    { label: 'Cookie Policy', href: '/cookies' },
    { label: 'GDPR', href: '/gdpr' },
  ],
};

const featureLinks = featureCatalog.map((f) => ({
  label: f.title,
  href: `/features/${f.id}`,
  icon: f.icon,
  description: f.shortPitch,
}));

const extraFeatureLinks = [
  {
    label: 'AI Declutter',
    href: '/ai-declutter-photos',
    icon: Eraser,
    description: 'Remove mess and personal items while keeping the furniture.',
  },
  {
    label: 'Remove Reflections',
    href: '/remove-photographer-reflections',
    icon: Eye,
    description: 'Erase photographer reflections from mirrors, glass and TVs.',
  },
  {
    label: 'Blur Family Photos',
    href: '/blur-family-photos',
    icon: ShieldOff,
    description: 'Auto-blur personal portraits and kids artwork for privacy.',
  },
];

const solutionLinks = [
  {
    label: 'Single Property Kit',
    href: '/single-property-kit',
    icon: Package,
    description: 'One-time kit for owners selling a single home.',
  },
  {
    label: 'For Real Estate Agents',
    href: '/real-estate-marketing-software',
    icon: Building2,
    description: 'Full suite built for agents and brokerages.',
  },
];

const socialLinks = [
  { icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61580739580163', label: 'Facebook' },
  { icon: Instagram, href: 'https://instagram.com', label: 'Instagram' },
  { icon: Twitter, href: 'https://twitter.com', label: 'Twitter' },
  { icon: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.88-2.89c0-1.6 1.29-2.89 2.88-2.89.3 0 .59.05.88.14v-3.5a6.25 6.25 0 0 0-.88-.07A6.34 6.34 0 0 0 3.15 15.6a6.34 6.34 0 0 0 6.34 6.34c3.5 0 6.34-2.84 6.34-6.34V8.78a8.01 8.01 0 0 0 4.76 1.57V6.9c-.36 0-.71-.07-1-.21z"/>
    </svg>
  ), href: 'https://tiktok.com', label: 'TikTok' },
];

export function Footer() {
  return (
    <footer className="bg-card border-t border-border/40">
      {/* Main Footer */}
      <div className="container max-w-7xl mx-auto px-4 py-12 sm:py-20">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-8 sm:gap-12">
          {/* Brand & Newsletter */}
          <div className="col-span-2 lg:col-span-2 space-y-6">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img src="/logo.png" alt="ListBright logo" className="h-16 md:h-20" width={147} height={80} />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed">
              The all-in-one AI suite for real estate listings. Photos, virtual staging,
              descriptions, video, microsite and flyer — every asset a listing needs, from a
              single upload.
            </p>
            
            {/* Newsletter */}
            <div className="space-y-3">
              <h3 className="font-semibold text-sm">Subscribe to our newsletter</h3>
              <div className="flex gap-2">
                <Input 
                  type="email" 
                  placeholder="Enter your email" 
                  className="flex-1"
                />
                <Button variant="default" size="sm">
                  Subscribe
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Get the latest updates and tips directly in your inbox.
              </p>
            </div>
          </div>

          {/* Product Links */}
          <div className="space-y-4">
            <h4 className="font-semibold text-xs uppercase tracking-widest text-muted-foreground">Product</h4>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href} 
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Features Links */}
          <div className="col-span-2 space-y-4">
            <h4 className="font-semibold text-xs uppercase tracking-widest text-muted-foreground">The Suite</h4>
            <ul className="space-y-4">
              {[...featureLinks, ...extraFeatureLinks].map((link) => {
                const Icon = link.icon;
                return (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="group flex items-start gap-3 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <span className="shrink-0 mt-0.5 w-7 h-7 rounded-md bg-muted flex items-center justify-center text-foreground/70 group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                        <Icon className="w-3.5 h-3.5" />
                      </span>
                      <span className="flex flex-col gap-0.5 min-w-0">
                        <span className="text-sm font-medium text-foreground/90 group-hover:text-foreground leading-tight">
                          {link.label}
                        </span>
                        {link.description && (
                          <span className="text-xs text-muted-foreground leading-snug line-clamp-2">
                            {link.description}
                          </span>
                        )}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Solutions Links */}
          <div className="space-y-4">
            <h4 className="font-semibold text-xs uppercase tracking-widest text-muted-foreground">Solutions</h4>
            <ul className="space-y-4">
              {solutionLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="group flex items-start gap-3 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <span className="shrink-0 mt-0.5 w-7 h-7 rounded-md bg-muted flex items-center justify-center text-foreground/70 group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                        <Icon className="w-3.5 h-3.5" />
                      </span>
                      <span className="flex flex-col gap-0.5 min-w-0">
                        <span className="text-sm font-medium text-foreground/90 group-hover:text-foreground leading-tight">
                          {link.label}
                        </span>
                        <span className="text-xs text-muted-foreground leading-snug line-clamp-2">
                          {link.description}
                        </span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Resources Links */}
          <div className="space-y-4">
            <h4 className="font-semibold text-xs uppercase tracking-widest text-muted-foreground">Resources</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href} 
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Free Tools Links */}
          <div className="space-y-4">
            <h4 className="font-semibold text-xs uppercase tracking-widest text-muted-foreground">Free Tools</h4>
            <ul className="space-y-3">
              {footerLinks.freeTools.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Comparisons Links */}
          <div className="space-y-4">
            <h4 className="font-semibold text-xs uppercase tracking-widest text-muted-foreground">Comparisons</h4>
            <ul className="space-y-3">
              {allComparisonLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div className="space-y-4">
            <h4 className="font-semibold text-xs uppercase tracking-widest text-muted-foreground">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href} 
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="col-span-2 sm:col-span-1 space-y-4">
            <h4 className="font-semibold text-xs uppercase tracking-widest text-muted-foreground">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Available Worldwide</span>
              </li>
              <li>
                <a 
                  href="mailto:sales@listbright.ai" 
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  sales@listbright.ai
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-border/40">
        <div className="container max-w-7xl mx-auto px-4 py-6 sm:py-8">
          <div className="flex flex-col items-center gap-4 md:flex-row md:justify-between">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} ListBright. All rights reserved.
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-2">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    aria-label={social.label}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm text-muted-foreground">
              {footerLinks.legal.slice(0, 2).map((link) => (
                <Link 
                  key={link.label}
                  to={link.href} 
                  className="hover:text-foreground transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
